import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Landmark, Eye, Lock } from "lucide-react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const TIPOS_CUENTA = [
  { value: "cheques", label: "Cheques" },
  { value: "ahorro", label: "Ahorro" },
  { value: "nomina", label: "Nómina" },
  { value: "otra", label: "Otra" },
];

export const maskNum = (v) => (v ? `•••• ${String(v).slice(-4)}` : "");
export const cuentaLabel = (c) => `${c.banco || "Cuenta"} · ${c.oculta ? "oculta" : c.clabe ? `CLABE ${maskNum(c.clabe)}` : `Cuenta ${maskNum(c.numero_cuenta)}`}${c.es_principal ? " · Principal" : ""}`;

const emptyCuenta = { banco: "", titular: "", clabe: "", numero_cuenta: "", tipo: "cheques", es_principal: false };

function Input({ label, name, value, onChange, placeholder, inputMode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(name, e.target.value)} placeholder={placeholder}
             inputMode={inputMode} data-testid={`cuenta-${name}`}
             className="w-full h-11 px-3 rounded-lg border border-[#CBD5E1] outline-none focus:ring-2 focus:ring-[#1E3A5F]" />
    </div>
  );
}

export function CuentaFormDialog({ open, onOpenChange, personaId, initial, defaultTitular, onSaved }) {
  const [f, setF] = useState(emptyCuenta);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(initial ? { ...emptyCuenta, ...initial } : { ...emptyCuenta, titular: defaultTitular || "" });
  }, [open, initial, defaultTitular]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { banco: f.banco, titular: f.titular, clabe: f.clabe, numero_cuenta: f.numero_cuenta, tipo: f.tipo, es_principal: f.es_principal };
      const { data } = initial?.id
        ? await api.put(`/cuentas/${initial.id}`, body)
        : await api.post(`/personas/${personaId}/cuentas`, body);
      toast.success("Cuenta guardada");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la cuenta");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cuenta-form-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Editar cuenta" : "Nueva cuenta bancaria"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-[#2A5C55] bg-[#EDF5F3] rounded-lg px-3 py-2 flex items-start gap-2" data-testid="cuenta-lock-note">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Estos datos se guardan cifrados. Nadie más los ve; solo se usan para llenar tus documentos.
          </p>
          <Input label="Banco (opcional)" name="banco" value={f.banco} onChange={set} placeholder="Ej. BBVA" />
          <Input label="Titular" name="titular" value={f.titular} onChange={set} />
          <Input label="CLABE interbancaria, 18 dígitos (opcional)" name="clabe" value={f.clabe} onChange={set} inputMode="numeric" />
          <Input label="Número de cuenta (opcional)" name="numero_cuenta" value={f.numero_cuenta} onChange={set} inputMode="numeric" />
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Tipo de cuenta</label>
            <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="cuenta-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPOS_CUENTA.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!f.es_principal} onChange={(e) => set("es_principal", e.target.checked)} data-testid="cuenta-es-principal" />
            Usar como cuenta principal
          </label>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="text-slate-600 px-4 py-2" data-testid="cuenta-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="bm-btn-primary" data-testid="cuenta-save">{saving ? "Guardando…" : "Guardar cuenta"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CuentaRow({ c, onEdit, onDelete }) {
  const [shown, setShown] = useState(null);
  const reveal = async () => {
    try {
      const { data } = await api.get(`/cuentas/${c.id}/revelar`);
      setShown(data);
      setTimeout(() => setShown(null), 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo mostrar"); }
  };
  const clabe = shown ? shown.clabe : c.clabe;
  const num = shown ? shown.numero_cuenta : c.numero_cuenta;
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[#E2DCD2] bg-white" data-testid={`cuenta-row-${c.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-[#EDF5F3] flex items-center justify-center shrink-0"><Landmark className="w-4 h-4 text-[#2A5C55]" /></div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm flex items-center gap-1.5">
            {c.banco || "Cuenta"}
            {c.es_principal && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-[#B8873D]" data-testid={`cuenta-principal-${c.id}`}><Star className="w-3 h-3 fill-current" /> Principal</span>}
          </div>
          <div className="text-xs text-slate-500 truncate" data-testid={`cuenta-datos-${c.id}`}>{c.titular} · {clabe ? `CLABE ${clabe}` : num ? `Cuenta ${num}` : "Sin número"}</div>
        </div>
      </div>
      <div className="flex items-center">
        {!c.oculta && (c.clabe || c.numero_cuenta) && (
          <button onClick={reveal} className="p-2 text-slate-500 hover:text-[#2A5C55]" data-testid={`cuenta-reveal-${c.id}`} aria-label="Ver un momento"><Eye className="w-4 h-4" /></button>
        )}
        <button onClick={() => onEdit(c)} className="p-2 text-slate-500 hover:text-[#2A5C55]" data-testid={`cuenta-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onDelete(c)} className="p-2 text-slate-400 hover:text-red-600" data-testid={`cuenta-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export function CuentasManager({ personaId, cuentas, defaultTitular, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const del = async (c) => {
    if (!window.confirm(`¿Eliminar la cuenta ${c.banco}?`)) return;
    await api.delete(`/cuentas/${c.id}`);
    toast.success("Cuenta eliminada");
    onChanged?.();
  };
  return (
    <div className="space-y-2" data-testid="cuentas-manager">
      {cuentas.length === 0 && <p className="text-sm text-slate-500" data-testid="cuentas-empty">Aún no hay cuentas registradas.</p>}
      {cuentas.map((c) => <CuentaRow key={c.id} c={c} onEdit={(x) => { setEditing(x); setOpen(true); }} onDelete={del} />)}
      <button type="button" onClick={() => { setEditing(null); setOpen(true); }} data-testid="cuenta-add-btn"
              className="w-full py-3 rounded-xl border-2 border-dashed border-[#CBD5E1] text-slate-600 hover:border-[#1E3A5F] hover:text-[#1E3A5F] font-semibold text-sm inline-flex items-center justify-center gap-2">
        <Plus className="w-4 h-4" /> Agregar cuenta bancaria
      </button>
      <CuentaFormDialog open={open} onOpenChange={setOpen} personaId={personaId} initial={editing}
                        defaultTitular={defaultTitular} onSaved={() => onChanged?.()} />
    </div>
  );
}
